/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { debounce } from '@ember/runloop';
import { isEmpty, isNone } from '@ember/utils';
import { action } from '@ember/object';
import ClipboardJS from 'clipboard';
import { htmlSafe } from '@ember/template';
import { assert } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import { SafeString } from '@ember/template/-private/handlebars';

const CLIPBOARD_LATENCY = 250;
const CLIPBOARD_BUTTON_CLASS = 'js-clipboard-button';
const CLIPBOARD_COPYABLE_CLASS = 'js-clipboard-copyable';
const CLIPBOARD_BUTTON_SELECTOR = `.${CLIPBOARD_BUTTON_CLASS}`;
const CLIPBOARD_COPYABLE_SELECTOR = `.${CLIPBOARD_COPYABLE_CLASS}`;

type CopyToClipboardBackgroundColor = 'paper' | 'gray-100' | 'gray-200' | '';

interface CopyToClipboardArgs {
  content?: string;
  background?: CopyToClipboardBackgroundColor;
  border?: boolean;
  buttonClassNames?: string;
  buttonContainerClassNames?: string;
  showDefaultCopyButton?: boolean;
  copyAnchor?: boolean;
  onCopy?: (_: ClipboardJS.Event) => unknown;
  iconSize?: 'sm' | 'md';
}

export default class CopyToClipboard extends Component<CopyToClipboardArgs> {
  get content() {
    return this.args.content ?? '';
  }

  get showDefaultCopyButton() {
    return this.args.showDefaultCopyButton ?? true;
  }

  get iconSize() {
    return this.args.iconSize ?? 'md';
  }

  isClipboardSupported = false;
  clipboard: ClipboardJS | null = null;
  container: HTMLDivElement | null = null;
  @tracked arrowStyle: string | SafeString | null = null;
  @tracked isShowingTooltip = false;

  CLIPBOARD_BUTTON_CLASS = CLIPBOARD_BUTTON_CLASS;
  CLIPBOARD_COPYABLE_CLASS = CLIPBOARD_COPYABLE_CLASS;

  get backgroundClass() {
    if (!isEmpty(this.args.background)) {
      return `copy-to-clipboard--bg-${this.args.background}`;
    }

    return '';
  }

  constructor(owner: unknown, args: CopyToClipboardArgs) {
    super(owner, args);

    // Make sure that ClipboardJS has the `isSupported` method and that it's a function.
    if (typeof ClipboardJS.isSupported === 'function') {
      this.isClipboardSupported = ClipboardJS.isSupported();
    }
  }

  @action
  handleInsert(el: HTMLDivElement) {
    this.container = el;

    if (!this.isClipboardSupported) {
      return;
    }

    let clipboardButton = this.container?.querySelector(CLIPBOARD_BUTTON_SELECTOR);
    if (clipboardButton == null) {
      assert(`[copy-to-clipboard] No element exists with the selector ${CLIPBOARD_BUTTON_SELECTOR}`);
    }

    let clipboard = new ClipboardJS(clipboardButton, {
      text: () => {
        if (!isEmpty(this.content)) {
          return this.content;
        } else {
          let copyable = this.container?.querySelector(CLIPBOARD_COPYABLE_SELECTOR) as HTMLElement | null;
          if (copyable == null) {
            assert(`[copy-to-clipboard] No copyable element exists with the selector ${CLIPBOARD_COPYABLE_SELECTOR}`);
          }

          if (this.args.copyAnchor === true) {
            if (copyable.tagName !== 'A') {
              assert('[copy-to-clipboard] copyAnchor was set to true, but copyable element is not an <a> tag');
            }

            return (copyable as HTMLLinkElement).href;
          }

          return copyable.innerText;
        }
      },
    });

    clipboard.on('success', (opts: ClipboardJS.Event) => {
      this.isShowingTooltip = true;

      this.args.onCopy?.(opts);
    });

    this.clipboard = clipboard;
  }

  willDestroy() {
    super.willDestroy();

    if (!isNone(this.clipboard)) {
      this.clipboard.destroy();
    }
  }

  setIsOpen(isOpen: boolean) {
    if (this.isDestroyed) {
      return;
    }

    this.isShowingTooltip = isOpen;

    if (!isOpen) {
      this.arrowStyle = null;
    }
  }

  @action
  findAnchor() {
    return this.container?.querySelector(CLIPBOARD_BUTTON_SELECTOR);
  }

  @action
  open() {
    debounce(this, this.setIsOpen, true, CLIPBOARD_LATENCY);
  }

  @action
  close() {
    debounce(this, this.setIsOpen, false, CLIPBOARD_LATENCY);
  }

  @action
  onEdgeOffset(alignment: string, triggerWidth: number) {
    if (alignment === 'left') {
      this.arrowStyle = htmlSafe(`left: ${triggerWidth / 2}px;`);
    }
    if (alignment === 'right') {
      this.arrowStyle = htmlSafe(`left: calc(100% - ${triggerWidth / 2}px);`);
    }
  }
}
